/**
 * Headless swimlane (flow / sequence) → SVG. The `renderProcessSvg` analog for
 * the swimlane views: computes the pure geometry (`swimlaneGeometry` with the
 * AFM `metricsMeasure` by default), composes the committed overlay
 * (`composeSwimlane`), and serializes ONE portable `<svg>` — inline styles
 * only, sanitized `idPrefix` on every def id, per-family arrowhead markers
 * with EXPLICIT fill/stroke (never `context-stroke`), 2dp coordinates, no CSS
 * classes, no `foreignObject`. Callers embedding several diagrams in one
 * document must pass DISTINCT prefixes (the default is a shared constant).
 */

import { metricsMeasure } from "../metrics.js";
import { composeSwimlane, type ComposeSwimlaneOptions } from "./compose-swimlane.js";
import { swimlaneGeometry } from "./swimlane-geometry.js";
import { attr, groupSvg, sanitizeIdPrefix } from "./render-svg.js";
import { defaultProcessTokens, type ProcessTokens } from "./tokens.js";
import type { SwimlaneSceneInput } from "./types.js";

export interface RenderSwimlaneSvgOptions extends ComposeSwimlaneOptions {
  /** Canvas backdrop; defaults to the token background. Pass `null` for none. */
  readonly background?: string | null;
  /** Namespace for `<defs>` ids (arrow markers). */
  readonly idPrefix?: string;
}

/** Committed SwimlaneView marker defs (1474-1493): the full solid arrow (seq)
 *  at 12px, the narrow ticks at 10px (re-entry 11px). Explicit fills. */
function swMarker(idPrefix: string, id: string, size: number, tick: boolean, fill: string): string {
  const d = tick ? "M 0 1 L 10 5 L 0 9 z" : "M 0 0 L 10 5 L 0 10 z";
  return (
    `<marker${attr("id", `${idPrefix}${id}`)} viewBox="0 0 10 10" refX="9" refY="5"` +
    `${attr("markerWidth", size)}${attr("markerHeight", size)} orient="auto">` +
    `<path d="${d}"${attr("fill", fill)}/></marker>`
  );
}

export function renderSwimlaneSvg(input: SwimlaneSceneInput, opts: RenderSwimlaneSvgOptions = {}): string {
  const tokens: ProcessTokens = opts.tokens ?? defaultProcessTokens();
  const measure = opts.measure ?? metricsMeasure;
  // Marker def ids already carry the `sw-` family prefix (MARKER_DEF_ID), so
  // the default namespace is empty — `sw-arrow`, not `sw-sw-arrow`. Custom
  // prefixes compose on top and stay distinct from the BPMN renderer's ids.
  const idPrefix = sanitizeIdPrefix(opts.idPrefix ?? "");
  const geom = swimlaneGeometry(input, measure, { ...opts, fontFamily: opts.fontFamily ?? tokens.fontFamily });
  const composed = composeSwimlane(input, geom, { ...opts, tokens });

  const w = Math.max(1, Math.round(composed.width));
  const h = Math.max(1, Math.round(composed.height));
  // v0.10.2 latency ruler lives left of x=0 — extend the viewBox left so it shows.
  const gutter = Math.max(0, Math.round(composed.leftGutter));
  const vbX = -gutter;
  const vbW = w + gutter;

  const defs: string[] = [];
  if (composed.markers.seq) defs.push(swMarker(idPrefix, "sw-arrow", 12, false, tokens.blockStroke));
  if (composed.markers.flowTick) defs.push(swMarker(idPrefix, "sw-flow-tick", 10, true, tokens.arrowFlow));
  if (composed.markers.flowTickErr) defs.push(swMarker(idPrefix, "sw-flow-tick-err", 10, true, tokens.arrowCatch));
  if (composed.markers.compTick) defs.push(swMarker(idPrefix, "sw-comp-tick", 10, true, tokens.compStroke));
  if (composed.markers.reentryTick) defs.push(swMarker(idPrefix, "sw-reentry-tick", 11, true, tokens.reentryArc));

  const bgColor = opts.background === undefined ? tokens.background : opts.background;
  const bg = bgColor ? `<rect${attr("x", vbX)} y="0"${attr("width", vbW)}${attr("height", h)}${attr("fill", bgColor)}/>` : "";

  const body = [...composed.nodes, ...composed.overlay]
    .map((g) => groupSvg(g, tokens.fontFamily, idPrefix))
    .join("");

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${vbW}" height="${h}" viewBox="${vbX} 0 ${vbW} ${h}" role="img">` +
    (defs.length ? `<defs>${defs.join("")}</defs>` : "") +
    bg +
    body +
    `</svg>`
  );
}
